// Marketing entry point — no Redux Provider, no Firebase, no auth.
// This is the lightweight bundle served on drahft.com.
// Dashboard/auth is served separately on studio.drahft.com.
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Toaster } from 'react-hot-toast';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';

import MainLayout from '@components/layouts/MainLayout';
import LoadingSpinner from '@components/common/LoadingSpinner';
import { APP_ROUTES } from '@config/routes';
import './styles/index.scss';

// Public pages only — no dashboard or auth pages
const HomePage = lazy(() => import('@pages/public/HomePage'));
const PricingPage = lazy(() => import('@pages/public/PricingPage'));
const FeaturesPage = lazy(() => import('@pages/public/FeaturesPage'));
const SolutionsPage = lazy(() => import('@pages/public/solutions'));
const SolutionsPhotographers = lazy(
  () => import('@pages/public/solutions/weddingPhotographers')
);
const SolutionsVideographers = lazy(
  () => import('@pages/public/solutions/weddingVideographers')
);
const SolutionsFlorists = lazy(() => import('@pages/public/solutions/florists'));
const SolutionsVenues = lazy(() => import('@pages/public/solutions/weddingVenues'));
const SolutionsCoordinators = lazy(
  () => import('@pages/public/solutions/weddingCoordinators')
);
const SolutionsDJs = lazy(() => import('@pages/public/solutions/dj_band_musicians'));
const SolutionsHairMakeup = lazy(() => import('@pages/public/solutions/hair_makeup'));
const SolutionsOfficiants = lazy(() => import('@pages/public/solutions/officiant'));
const SolutionsCalligraphers = lazy(() => import('@pages/public/solutions/calligrapher'));
const SolutionsDecorRentals = lazy(() => import('@pages/public/solutions/decor_rentals'));
const SolutionsPhotoBooth = lazy(() => import('@pages/public/solutions/photobooth'));
const SolutionsTransportation = lazy(() => import('@pages/public/solutions/transportation'));
const SolutionsBridalBoutiques = lazy(
  () => import('@pages/public/solutions/bridal_boutique')
);
const TermsConditions = lazy(() => import('@pages/public/TermsConditions'));
const PrivacyPolicy = lazy(() => import('@pages/public/PrivacyPolicy'));
const ContactPage = lazy(() => import('@pages/public/ContactPage'));
const RoadmapPage = lazy(() => import('@pages/public/RoadmapPage'));
const ChangelogPage = lazy(() => import('@pages/public/ChangelogPage'));
const DocumentationPage = lazy(() => import('@pages/public/DocumentationPage'));
const SupportPage = lazy(() => import('@pages/public/SupportPage'));
const AffiliateProgramPage = lazy(() => import('@pages/public/AffiliateProgramPage'));
const CommunityPage = lazy(() => import('@pages/public/CommunityPage'));
const RefundPolicyPage = lazy(() => import('@pages/public/RefundPolicyPage'));
const DisclaimerPage = lazy(() => import('@pages/public/DisclaimerPage'));

const MarketingRoutes = () => (
  <Suspense fallback={<LoadingSpinner fullScreen />}>
    <Routes>
      <Route element={<MainLayout />}>
        <Route path={APP_ROUTES.HOME} element={<HomePage />} />
        <Route path={APP_ROUTES.PRICING} element={<PricingPage />} />
        <Route path={APP_ROUTES.FEATURES} element={<FeaturesPage />} />
        <Route path={APP_ROUTES.SOLUTIONS} element={<SolutionsPage />} />
        <Route path={APP_ROUTES.SOLUTIONS_PHOTOGRAPHERS} element={<SolutionsPhotographers />} />
        <Route path={APP_ROUTES.SOLUTIONS_VIDEOGRAPHERS} element={<SolutionsVideographers />} />
        <Route path={APP_ROUTES.SOLUTIONS_FLORISTS} element={<SolutionsFlorists />} />
        <Route path={APP_ROUTES.SOLUTIONS_VENUES} element={<SolutionsVenues />} />
        <Route path={APP_ROUTES.SOLUTIONS_COORDINATORS} element={<SolutionsCoordinators />} />
        <Route path={APP_ROUTES.SOLUTIONS_DJS} element={<SolutionsDJs />} />
        <Route path={APP_ROUTES.SOLUTIONS_HAIR_MAKEUP} element={<SolutionsHairMakeup />} />
        <Route path={APP_ROUTES.SOLUTIONS_OFFICIANTS} element={<SolutionsOfficiants />} />
        <Route path={APP_ROUTES.SOLUTIONS_CALLIGRAPHERS} element={<SolutionsCalligraphers />} />
        <Route path={APP_ROUTES.SOLUTIONS_DECOR_RENTALS} element={<SolutionsDecorRentals />} />
        <Route path={APP_ROUTES.SOLUTIONS_PHOTO_BOOTH} element={<SolutionsPhotoBooth />} />
        <Route path={APP_ROUTES.SOLUTIONS_TRANSPORTATION} element={<SolutionsTransportation />} />
        <Route
          path={APP_ROUTES.SOLUTIONS_BRIDAL_BOUTIQUES}
          element={<SolutionsBridalBoutiques />}
        />
        <Route path={APP_ROUTES.TERMS} element={<TermsConditions />} />
        <Route path={APP_ROUTES.PRIVACY} element={<PrivacyPolicy />} />
        <Route path={APP_ROUTES.CONTACT} element={<ContactPage />} />
        <Route path={APP_ROUTES.ROADMAP} element={<RoadmapPage />} />
        <Route path={APP_ROUTES.CHANGELOG_PUBLIC} element={<ChangelogPage />} />
        <Route path={APP_ROUTES.DOCUMENTATION} element={<DocumentationPage />} />
        <Route path={APP_ROUTES.SUPPORT} element={<SupportPage />} />
        <Route path={APP_ROUTES.AFFILIATE_PROGRAM} element={<AffiliateProgramPage />} />
        <Route path={APP_ROUTES.COMMUNITY} element={<CommunityPage />} />
        <Route path={APP_ROUTES.REFUND_POLICY} element={<RefundPolicyPage />} />
        <Route path={APP_ROUTES.DISCLAIMER} element={<DisclaimerPage />} />
      </Route>

      {/* Any unknown path sends visitors to the homepage */}
      <Route path="*" element={<Navigate to={APP_ROUTES.HOME} replace />} />
    </Routes>
  </Suspense>
);

createRoot(document.getElementById('root')!).render(
  <BrowserRouter
    future={{
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    }}
  >
    <MarketingRoutes />
    <Toaster
      position="top-right"
      toastOptions={{
        className: 'font-sans',
      }}
    />
    <Analytics />
    <SpeedInsights />
  </BrowserRouter>
);
